import type {
  AccommodationImportIssue,
  AccommodationImportItem,
} from './types'

export interface ParsedAccommodationCsv {
  rows: Array<AccommodationImportItem & { line: number }>
  issues: AccommodationImportIssue[]
}

interface CsvRecord {
  cells: string[]
  line: number
}

function detectDelimiter(input: string): string {
  const firstLine = input.replace(/^\uFEFF/, '').split(/\r?\n/, 1)[0] ?? ''
  const candidates = [',', ';', '\t']
  return candidates.reduce((best, candidate) => (
    firstLine.split(candidate).length > firstLine.split(best).length ? candidate : best
  ), ',')
}

function records(input: string, delimiter: string): CsvRecord[] {
  const result: CsvRecord[] = []
  let cells: string[] = []
  let value = ''
  let quoted = false
  let line = 1
  let recordLine = 1

  const pushRecord = () => {
    cells.push(value)
    result.push({ cells, line: recordLine })
    cells = []
    value = ''
    recordLine = line
  }

  for (let index = 0; index < input.length; index += 1) {
    const character = input[index]
    const next = input[index + 1]

    if (quoted) {
      if (character === '"' && next === '"') {
        value += '"'
        index += 1
      } else if (character === '"') {
        quoted = false
      } else {
        value += character
        if (character === '\n') line += 1
      }
      continue
    }

    if (character === '"' && value.length === 0) {
      quoted = true
    } else if (character === delimiter) {
      cells.push(value)
      value = ''
    } else if (character === '\r') {
      if (next === '\n') index += 1
      line += 1
      pushRecord()
    } else if (character === '\n') {
      line += 1
      pushRecord()
    } else {
      value += character
    }
  }

  if (value || cells.length > 0 || (input.length > 0 && !/[\r\n]$/.test(input))) {
    pushRecord()
  }

  return result.filter((record) => record.cells.some((cell) => cell.trim() !== ''))
}

function headerKey(value: string): string {
  return value.trim().toLowerCase().replace(/[\s_-]+/g, '')
}

export function parseAccommodationCsv(input: string): ParsedAccommodationCsv {
  const csvRecords = records(input.replace(/^\uFEFF/, ''), detectDelimiter(input))
  if (csvRecords.length === 0) {
    return { rows: [], issues: [{ line: 1, reason: 'The CSV file is empty.' }] }
  }

  const headers = csvRecords[0].cells.map(headerKey)
  const emailIndex = headers.findIndex((header) =>
    ['email', 'participantemail', 'username'].includes(header),
  )
  const roomIndex = headers.findIndex((header) =>
    ['roomnumber', 'room'].includes(header),
  )
  if (emailIndex < 0 || roomIndex < 0) {
    return {
      rows: [],
      issues: [{ line: csvRecords[0].line, reason: 'Required columns are email and room_number.' }],
    }
  }

  const rows: Array<AccommodationImportItem & { line: number }> = []
  const issues: AccommodationImportIssue[] = []
  const seen = new Set<string>()

  csvRecords.slice(1).forEach((record) => {
    const email = (record.cells[emailIndex] ?? '').trim()
    const roomNumber = (record.cells[roomIndex] ?? '').trim()
    if (!email && !roomNumber) return

    const normalizedEmail = email.toLowerCase()
    if (!email) {
      issues.push({ line: record.line, reason: 'Participant email is missing.' })
      return
    }
    if (!roomNumber) {
      issues.push({ line: record.line, participant_email: email, reason: 'Room number is missing.' })
      return
    }
    if (email.length > 255) {
      issues.push({ line: record.line, participant_email: email, reason: 'Participant email is too long.' })
      return
    }
    if (roomNumber.length > 50) {
      issues.push({ line: record.line, participant_email: email, reason: 'Room number is too long.' })
      return
    }
    if (seen.has(normalizedEmail)) {
      issues.push({ line: record.line, participant_email: email, reason: 'Duplicate participant email.' })
      return
    }

    seen.add(normalizedEmail)
    rows.push({ line: record.line, participant_email: email, room_number: roomNumber })
  })

  return { rows, issues }
}
